import { customerProjection } from '@sqi/shared-schemas';
import type { OemRecord, OemWorkspace } from '../../lib/oem-client';
import { routeGroups } from './route-catalog';
import { latestVisibleByRoot, parseDeadline } from './task-dashboard-model';
export const milestoneStages = [
  { key: 'brief', label: 'ข้อมูลสินค้า', kinds: ['brief'] },
  { key: 'samples', label: 'ตัวอย่าง', kinds: ['sample'] },
  { key: 'design', label: 'ฉลากและบรรจุภัณฑ์', kinds: ['packaging', 'artwork'] },
  { key: 'commercial', label: 'ใบเสนอราคาและ PO', kinds: ['quote', 'po'] },
  { key: 'finance', label: 'หลักฐานการชำระเงิน', kinds: ['receipt', 'allocation'] },
  { key: 'work', label: 'งานถัดไป', kinds: ['task'] },
  { key: 'handover', label: 'ข้อมูลส่งต่อที่มองเห็น', kinds: ['order', 'handover'] },
];
const pages: Record<string, { page: string; label: string }> = {
  brief: { page: 'brief', label: 'เปิดข้อมูลสินค้า' },
  packaging: { page: 'packaging', label: 'ดูบรรจุภัณฑ์' },
  quote: { page: 'quotes', label: 'ดูรายการใบเสนอราคา' },
  po: { page: 'po', label: 'ดูรายการ PO' },
  receipt: { page: 'payments', label: 'เปิดหน้าการชำระเงิน' },
  allocation: { page: 'payments', label: 'เปิดหน้าการชำระเงิน' },
  order: { page: 'handover', label: 'เปิดหน้าส่งต่อ' },
  handover: { page: 'handover', label: 'เปิดหน้าส่งต่อ' },
};
const permitted = new Set(
  routeGroups('customer').flatMap((group) => group.pages.map((page) => page.key)),
);
function route(record: Pick<OemRecord, 'kind' | 'id'>) {
  if (record.kind === 'sample')
    return { path: `samples/${encodeURIComponent(record.id)}`, label: 'เปิดตัวอย่างฉบับนี้' };
  if (record.kind === 'artwork')
    return {
      path: `artwork/${encodeURIComponent(record.id)}`,
      label: 'เปิดแบบฉบับนี้',
    };
  const destination = pages[record.kind];
  return destination && permitted.has(destination.page)
    ? { path: destination.page, label: destination.label }
    : undefined;
}
function label(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}
function metadataDate(value: unknown) {
  const parsed = parseDeadline(value);
  return parsed.kind === 'date' ? { kind: 'invalid' as const } : parsed;
}
export function customerTimeline(workspace: OemWorkspace, sku = '', category = 'all') {
  const allowed = new Set(milestoneStages.flatMap((stage) => stage.kinds));
  const match = (skuId: string | null) =>
    !sku || (sku === 'project' ? skuId === null : skuId === sku);
  const entries = latestVisibleByRoot(
    workspace.records.filter((record) => allowed.has(record.kind)),
  )
    .filter((record) => match(record.skuId))
    .map((record) => {
      const projected = customerProjection(record.content);
      return {
        id: record.id,
        kind: record.kind,
        skuId: record.skuId,
        version: record.version,
        state: record.state,
        label: label(projected.label, record.kind),
        date: metadataDate(record.createdAt),
        nextAction:
          record.kind === 'task'
            ? label(projected.nextAction, 'ยังไม่ระบุขั้นตอนถัดไป')
            : undefined,
        route: route(record),
      };
    });
  const stages = milestoneStages
    .filter((stage) => category === 'all' || stage.key === category)
    .map((stage) => ({
      ...stage,
      entries: entries.filter((entry) => stage.kinds.includes(entry.kind)),
    }));
  const frozen = workspace.order
    ? {
        id: workspace.order.id,
        date: metadataDate(workspace.order.confirmedAt),
        versions: (workspace.order.summary?.versions ?? [])
          .filter(
            (version) =>
              allowed.has(version.kind) &&
              match(version.skuId) &&
              (category === 'all' ||
                milestoneStages
                  .find((stage) => stage.key === category)
                  ?.kinds.includes(version.kind)),
          )
          .map((version) => ({
            id: version.id,
            kind: version.kind,
            version: version.version,
            label: label(customerProjection({ label: version.label }).label, version.kind),
            skuId: version.skuId,
          })),
      }
    : null;
  return { stages, frozen, total: stages.reduce((sum, stage) => sum + stage.entries.length, 0) };
}
